import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Modal, SelectField, TextField } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import useProvisionerProducts from "../../hooks/useProvisionerProducts";
import useProvisionedSubscriptionMutations from "../../hooks/useProvisionedSubscriptionMutations";
import { productLabel, serverErrorMessage, toProvisionedSubscription } from "../../functions/provisionedSubscription";
import IncludedProductGroups from "./IncludedProductGroups";
import * as modalStyles from "./ProvisioningStyles.module.scss";
import * as styles from "./CreateSubscription.module.scss";

const NO_PRODUCT = "";

const messages = defineMessages( {
	title: {
		id: "provisioning.create.title",
		defaultMessage: "New subscription",
	},
	ariaLabel: {
		id: "provisioning.create.ariaLabel",
		defaultMessage: "Create a subscription for a customer",
	},
	emailLabel: {
		id: "provisioning.create.emailLabel",
		defaultMessage: "Customer email",
	},
	productLabel: {
		id: "provisioning.create.productLabel",
		defaultMessage: "Product",
	},
	productPlaceholder: {
		id: "provisioning.create.productPlaceholder",
		defaultMessage: "Select a product",
	},
	productsLoadError: {
		id: "provisioning.create.productsLoadError",
		defaultMessage: "We could not load your products. Please try again.",
	},
	siteLabel: {
		id: "provisioning.create.siteLabel",
		defaultMessage: "Site (optional)",
	},
	firstNameLabel: {
		id: "provisioning.create.firstNameLabel",
		defaultMessage: "First name (optional)",
	},
	lastNameLabel: {
		id: "provisioning.create.lastNameLabel",
		defaultMessage: "Last name (optional)",
	},
	cancel: {
		id: "provisioning.create.cancel",
		defaultMessage: "Cancel",
	},
	submit: {
		id: "provisioning.create.submit",
		defaultMessage: "Create subscription",
	},
	error: {
		id: "provisioning.create.error",
		defaultMessage: "Could not create the subscription. Please try again.",
	},
} );

/**
 * Builds the request body, leaving out the optional fields the user left empty.
 *
 * @param {Object} fields The trimmed form fields.
 *
 * @returns {Object} The create payload.
 */
function toPayload( { email, productCode, site, firstName, lastName } ) {
	const payload = { customerEmail: email, productCode };

	if ( site ) {
		payload.site = site;
	}
	if ( firstName ) {
		payload.firstName = firstName;
	}
	if ( lastName ) {
		payload.lastName = lastName;
	}

	return payload;
}

/**
 * Modal for provisioning a subscription for a customer.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function CreateProvisionedSubscriptionModal( { isOpen, onClose, onCreated, intl } ) {
	const { products, isError: productsFailed } = useProvisionerProducts();
	const { create } = useProvisionedSubscriptionMutations();

	const [ email, setEmail ]           = useState( "" );
	const [ productCode, setProduct ]   = useState( NO_PRODUCT );
	const [ site, setSite ]             = useState( "" );
	const [ firstName, setFirstName ]   = useState( "" );
	const [ lastName, setLastName ]     = useState( "" );

	const options = useMemo( () => [
		{ value: NO_PRODUCT, label: intl.formatMessage( messages.productPlaceholder ) },
		...products.map( product => ( {
			value: product.productCode,
			label: productLabel( { name: product.name, code: product.productCode } ),
		} ) ),
	], [ products, intl ] );

	const selectedProduct = products.find( product => product.productCode === productCode );

	const reset = useCallback( () => {
		setEmail( "" );
		setProduct( NO_PRODUCT );
		setSite( "" );
		setFirstName( "" );
		setLastName( "" );
		create.reset();
	}, [ create ] );

	// Escape and the backdrop still close the modal while Cancel is disabled. Resetting an in-flight
	// mutation detaches the `onSuccess` below, so the subscription would be created without a result.
	const handleClose = useCallback( () => {
		if ( create.isPending ) {
			return;
		}
		reset();
		onClose();
	}, [ create, reset, onClose ] );

	const submitDisabled = create.isPending || ! email.trim() || productCode === NO_PRODUCT;

	const handleSubmit = ( event ) => {
		event.preventDefault();
		if ( submitDisabled ) {
			return;
		}

		const data = toPayload( {
			email: email.trim(),
			productCode,
			site: site.trim(),
			firstName: firstName.trim(),
			lastName: lastName.trim(),
		} );

		create.mutate( { data }, {
			onSuccess: ( dto ) => {
				reset();
				onCreated( toProvisionedSubscription( dto ) );
			},
		} );
	};

	return (
		<Modal
			isOpen={ isOpen }
			onClose={ handleClose }
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<form onSubmit={ handleSubmit }>
					<Modal.Title as="h2" size="3">
						<FormattedMessage { ...messages.title } />
					</Modal.Title>
					<div className={ modalStyles.modalBody }>
						<TextField
							id="provisioning-create-email"
							name="customerEmail"
							type="email"
							label={ intl.formatMessage( messages.emailLabel ) }
							value={ email }
							required={ true }
							onChange={ ( e ) => setEmail( e.target.value ) }
						/>
						<div>
							<SelectField
								id="provisioning-create-product"
								name="productCode"
								label={ intl.formatMessage( messages.productLabel ) }
								value={ productCode }
								onChange={ setProduct }
								options={ options }
							/>
							{ productsFailed && (
								<Alert variant="error">
									<FormattedMessage { ...messages.productsLoadError } />
								</Alert>
							) }
							{ selectedProduct && (
								<IncludedProductGroups productGroups={ selectedProduct.includes } />
							) }
						</div>
						<TextField
							id="provisioning-create-site"
							name="site"
							type="url"
							label={ intl.formatMessage( messages.siteLabel ) }
							value={ site }
							onChange={ ( e ) => setSite( e.target.value ) }
						/>
						<div className={ styles.nameFields }>
							<TextField
								id="provisioning-create-first-name"
								name="firstName"
								type="text"
								label={ intl.formatMessage( messages.firstNameLabel ) }
								value={ firstName }
								onChange={ ( e ) => setFirstName( e.target.value ) }
							/>
							<TextField
								id="provisioning-create-last-name"
								name="lastName"
								type="text"
								label={ intl.formatMessage( messages.lastNameLabel ) }
								value={ lastName }
								onChange={ ( e ) => setLastName( e.target.value ) }
							/>
						</div>
						{ create.isError && (
							<Alert variant="error">
								{ serverErrorMessage( create.error ) || <FormattedMessage { ...messages.error } /> }
							</Alert>
						) }
					</div>
					<div className={ modalStyles.modalActions }>
						<Button variant="secondary" onClick={ handleClose } disabled={ create.isPending }>
							<FormattedMessage { ...messages.cancel } />
						</Button>
						<Button
							type="submit"
							variant="primary"
							isLoading={ create.isPending }
							disabled={ submitDisabled }
						>
							<FormattedMessage { ...messages.submit } />
						</Button>
					</div>
				</form>
			</Modal.Panel>
		</Modal>
	);
}

CreateProvisionedSubscriptionModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onCreated: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( CreateProvisionedSubscriptionModal );
